import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useVocabulary } from './contexts/VocabularyContext';
import './css/Vocabulary.css';
import './css/VocabularyQuiz.css';

const VocabularyQuizResult = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { quizId } = useParams();
  const { quizApi } = useVocabulary();

  const [result, setResult] = useState(null);
  const [onlyWrong, setOnlyWrong] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const data = await quizApi.getResult(quizId);
      if (data.success) setResult(data);
      setLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizId]);

  if (loading) {
    return <div className="vocab-container"><p className="vocab-empty">{t('vocabulary.loading')}</p></div>;
  }
  if (!result) {
    return (
      <div className="vocab-container">
        <p className="vocab-empty">{t('vocabulary.quiz.notFound')}</p>
        <button className="vocab-btn" onClick={() => navigate('/vocabulary/quiz')}>
          {t('backToHome')}
        </button>
      </div>
    );
  }

  const { quiz, questions } = result;
  const shown = onlyWrong ? questions.filter(q => !q.is_correct) : questions;
  const wrongCount = questions.filter(q => !q.is_correct).length;

  /**
   * 종이 시험을 수기로 채점하면 user_answer 에 O/X 만 들어온다.
   * 무엇을 골랐는지·썼는지는 종이에만 있으므로 '수기 채점'으로 표시한다.
   * 선택지 번호로 해석하면 '(미응답)'이 되어 답을 안 쓴 것처럼 오해된다.
   */
  const renderUserAnswer = (q) => {
    if (q.user_answer === 'O' || q.user_answer === 'X') {
      return t('vocabulary.quiz.manualGraded');
    }
    if (q.format !== 'multiple_choice') {
      return q.user_answer || t('vocabulary.quiz.noAnswer');
    }
    return q.choices?.[Number(q.user_answer)] ?? t('vocabulary.quiz.noAnswer');
  };

  const formatTime = (sec) => {
    if (!sec && sec !== 0) return '-';
    return `${Math.floor(sec / 60)}${t('minutes')} ${sec % 60}${t('vocabulary.quiz.secondsSuffix')}`;
  };

  /*
   * 날짜는 문항마다 붙인다.
   * 출제는 가중 샘플링이라 문항이 날짜순으로 놓이지 않는다. 앞 항목과 비교해
   * 날짜 머리글을 끼우면 거의 모든 문항 사이에 띠가 생겨 목록이 계속 끊긴다.
   */

  return (
    <div className="vocab-container">
      <div className="vocab-header">
        <h2>{quiz.title || t('vocabulary.quiz.untitled')}</h2>
        <button className="vocab-btn" onClick={() => navigate('/vocabulary/quiz')}>
          {t('vocabulary.quiz.backToList')}
        </button>
      </div>

      <div className="result-summary">
        <div className={`result-score ${quiz.score >= 80 ? 'good' : quiz.score >= 60 ? 'mid' : 'low'}`}>
          <strong>{quiz.score}</strong>
          <span>{t('vocabulary.quiz.scoreSuffix')}</span>
        </div>
        <dl className="result-stats">
          <div><dt>{t('vocabulary.quiz.correctCount')}</dt><dd>{quiz.correct_count} / {quiz.question_count}</dd></div>
          <div><dt>{t('vocabulary.quiz.wrongCount')}</dt><dd>{wrongCount}</dd></div>
          <div><dt>{t('vocabulary.quiz.duration')}</dt><dd>{formatTime(quiz.duration_seconds)}</dd></div>
        </dl>
      </div>

      {wrongCount > 0 && (
        <p className="result-review-hint">
          {t('vocabulary.quiz.addedToWrongNotes', { count: wrongCount })}
          <button className="vocab-btn" onClick={() => navigate('/vocabulary/review')}>
            {t('vocabulary.tabReview')}
          </button>
        </p>
      )}

      <label className="vocab-checkbox result-filter">
        <input type="checkbox" checked={onlyWrong} onChange={(e) => setOnlyWrong(e.target.checked)} />
        {t('vocabulary.quiz.onlyWrong')}
      </label>

      <ul className="result-list">
        {shown.length === 0 && <li className="vocab-empty">{t('vocabulary.quiz.allCorrect')}</li>}
        {shown.map(q => (
          <React.Fragment key={q.question_id}>
            <li className={`result-item ${q.is_correct ? 'correct' : 'wrong'}`}>
              <span className="result-mark">{q.is_correct ? '✓' : '✕'}</span>
              <div className="result-body">
                <p className="result-prompt">
                  <span className="result-index">{q.order_index + 1}.</span> {q.prompt}
                  {q.study_date && <span className="result-date">{q.study_date.slice(5)}</span>}
                </p>

                {/*
                  * 객관식이면 고른 선택지 텍스트를 보여준다.
                  * 단 종이 시험을 수기로 채점한 경우 O/X만 남아 있어 무엇을 골랐는지 알 수 없다.
                  */}
                <p className="result-answer-row">
                  <span className="result-label">{t('vocabulary.quiz.yourAnswer')}</span>
                  <span className={q.is_correct ? 'ok' : 'ng'}>
                    {renderUserAnswer(q)}
                  </span>
                </p>

                {/* 맞았더라도 정답 전문을 항상 노출한다 — 부분 정답으로 통과했을 수 있다 */}
                <p className="result-answer-row">
                  <span className="result-label">{t('vocabulary.quiz.correctAnswer')}</span>
                  <span className="ok">{q.correct_answer}</span>
                </p>

                <p className="result-word-pair">
                  {q.word_text} — {q.meaning_text}
                </p>
              </div>
            </li>
          </React.Fragment>
        ))}
      </ul>
    </div>
  );
};

export default VocabularyQuizResult;
